import { useState, useEffect, useRef } from "react";
import { parse } from "svgson";
import { useSelector } from "react-redux";
import { fetchSVGContentFromStorage } from "../../lib/firebase/firebase";
import { sanitizeSvg } from "./helpers/importSvg";
import { splitSvgLayers, joinSvgLayers, extractLayerColors } from "./helpers/svgLayers";

const useSvgUpdater = () => {
  const [svg, setSvg] = useState();
  // Ref mirror of `svg` so `updateSvg` can support functional updaters —
  // and so back-to-back synchronous calls (e.g. multiple paintBucketV2
  // diff-mask commits in one Potrace queue flush) resolve against the
  // latest value rather than a stale closure.
  const svgRef = useRef();
  const [svgJoined, setSvgJoined] = useState();
  const [svgHistory, setSvgHistory] = useState(new Array(50));
  const [historyIndex, setHistoryIndex] = useState(0);
  const [undoActions, setUndoActions] = useState(0);
  const [redoActions, setRedoActions] = useState(0);
  const [colors, setColors] = useState();
  const [changesMade, setChangesMade] = useState(false);
  const [svgError, setSvgError] = useState(null);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [rawSvgString, setRawSvgString] = useState(null);

  const currentFile = useSelector((state) => state.files.currentFile);

  useEffect(() => {
    svgRef.current = svg;
  }, [svg]);

  useEffect(() => {
    let cancelled = false;
    setSvgError(null);
    setRawSvgString(null);
    svgRef.current = undefined;
    setSvg(undefined);
    setSvgJoined(undefined);
    setColors(undefined);
    setSvgHistory(new Array(50));
    setHistoryIndex(0);
    setUndoActions(0);
    setRedoActions(0);
    setChangesMade(false);

    const timeout = setTimeout(() => {
      cancelled = true;
      setSvgError("Loading took too long. Check your connection and try again.");
    }, 30000);

    const fetchAndParseSVG = async () => {
      try {
        if (!currentFile) throw new Error("No design is selected. Return to your files and open it again.");
        // Newly converted designs already have their SVG in memory. Reopening
        // a saved design still fetches Storage, with a bounded wait and retry.
        const source = currentFile.svgData || await fetchSVGContentFromStorage(currentFile);
        if (cancelled) return;
        const clean = sanitizeSvg(source);
        const parsed = await parse(clean);
        if (cancelled) return;
        const split = splitSvgLayers(parsed);
        const joined = joinSvgLayers(split);
        setRawSvgString(clean);
        svgRef.current = split;
        setSvg(split);
        setSvgJoined(joined);
        setColors(extractLayerColors(joined));
        const history = new Array(50);
        history[0] = JSON.parse(JSON.stringify(split));
        setSvgHistory(history);
        setHistoryIndex(1);
        setUndoActions(1);
      } catch (error) {
        if (!cancelled) {
          console.error("Error fetching and parsing SVG:", error);
          setSvgError("Could not open this design: " + (error.message || "Unknown error"));
        }
      } finally {
        clearTimeout(timeout);
      }
    };

    fetchAndParseSVG();
    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [currentFile, loadAttempt]);

  const updateSVGJoined = (split) => {
    const joined = joinSvgLayers(split);
    setColors(extractLayerColors(joined));
    setSvgJoined(joined);
  };

  const updateSvg = (newSvgOrFn, { skipHistory, replaceHistory } = {}) => {
    const newSvg =
      typeof newSvgOrFn === "function"
        ? newSvgOrFn(svgRef.current)
        : newSvgOrFn;
    if (!newSvg) return;
    if (!skipHistory) {
      if (undoActions > 0) {
        setChangesMade(true);
      }
      const replace = replaceHistory && undoActions > 1 && redoActions === 0;
      setUndoActions(replace ? undoActions : Math.min(undoActions + 1, 50));
      setRedoActions(0);
      let items = Object.assign({}, svgHistory);
      let item = JSON.parse(JSON.stringify(newSvg));
      items[replace ? (historyIndex + 49) % 50 : historyIndex] = item;
      if (!replace) setHistoryIndex((historyIndex + 1) % 50);
      setSvgHistory(items);
    }
    svgRef.current = newSvg;
    setSvg(newSvg);
    updateSVGJoined(newSvg);
  };

  const mod = (n, m) => {
    return ((n % m) + m) % m;
  };

  const undo = () => {
    if (undoActions > 1) {
      let item = JSON.parse(
        JSON.stringify(svgHistory[mod(historyIndex - 2, 50)])
      );
      svgRef.current = item;
      setSvg(item);
      updateSVGJoined(item);
      setHistoryIndex(mod(historyIndex - 1, 50));
      setUndoActions(undoActions - 1);
      setRedoActions(redoActions + 1);
    }
  };

  const redo = () => {
    if (redoActions > 0) {
      let item = JSON.parse(JSON.stringify(svgHistory[historyIndex % 50]));
      svgRef.current = item;
      setSvg(item);
      updateSVGJoined(item);
      setHistoryIndex((historyIndex + 1) % 50);
      setRedoActions(redoActions - 1);
      setUndoActions(undoActions + 1);
    }
  };

  const undoAll = () => {
    if (undoActions > 0) {
      let item = JSON.parse(JSON.stringify(svgHistory[0]));
      svgRef.current = item;
      setSvg(item);
      updateSVGJoined(item);
      setHistoryIndex(1);
      setUndoActions(1);
      setRedoActions(undoActions - 1);
    }
  };

  return {
    svg,
    svgJoined,
    svgHistory,
    historyIndex,
    undoActions,
    redoActions,
    colors,
    changesMade,
    svgError,
    retryLoad: () => setLoadAttempt((attempt) => attempt + 1),
    rawSvgString,
    setChangesMade,
    setHistoryIndex,
    setUndoActions,
    setRedoActions,
    setColors,
    updateSvg,
    undo,
    undoAll,
    redo,
    currentFile,
  };
};

export default useSvgUpdater;
